import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import type { SpotCategory } from '@ridehub/shared';
import { CATEGORIES, TAGS } from '@ridehub/shared';
import { useGeolocationContext } from '../../contexts/GeolocationContext.js';
import { Button } from '../../components/Button.js';
import { Input } from '../../components/Input.js';
import { useSpotRegistration } from './useSpotRegistration.js';

async function fetchAddressFromCoords(lat: number, lng: number): Promise<string> {
  const res = await fetch(
    `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&accept-language=ja`,
    { headers: { 'Accept-Language': 'ja' } }
  );
  const data = await res.json();
  const a = data.address ?? {};
  // 都道府県 + 市区町村 + 町域 を組み立てる
  return [a.state, a.city ?? a.town ?? a.village, a.suburb ?? a.neighbourhood, a.road]
    .filter(Boolean)
    .join('');
}

export function SpotRegistrationForm() {
  const navigate = useNavigate();
  const { latitude, longitude } = useGeolocationContext();

  // 座標が取得できたら住所を自動入力（初回のみ）
  useEffect(() => {
    if (latitude === null || longitude === null) return;
    setFetchedAddress(null); // 取得中
    fetchAddressFromCoords(latitude, longitude)
      .then((fetched) => {
        setFetchedAddress(fetched || '');
        if (fetched) setAddress(fetched);
      })
      .catch(() => setFetchedAddress(''));
  }, [latitude, longitude]);
  const { register, loading, error, duplicateWarning, clearWarning } = useSpotRegistration();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [address, setAddress] = useState('');
  const [fetchedAddress, setFetchedAddress] = useState<string | null>(null);
  const [category, setCategory] = useState<SpotCategory>('other');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (latitude === null || longitude === null) return;

    const spot = await register({
      name,
      description: description || undefined,
      address: address || undefined,
      latitude,
      longitude,
      category,
      tags: selectedTags.length > 0 ? selectedTags : undefined,
    });

    if (spot) {
      navigate(`/spots/${spot.id}`, { replace: true });
    }
  };

  return (
    <div className="p-4 max-w-lg mx-auto">
      <h1 className="text-xl font-bold mb-6">スポットを登録</h1>

      {latitude !== null && longitude !== null && (
        <div className="bg-primary-50 border border-primary-100 rounded-lg px-4 py-3 mb-4 flex items-start gap-2.5">
          <span className="text-primary-300 flex-shrink-0 mt-0.5">📍</span>
          <div>
            <p className="text-xs text-primary font-medium mb-0.5">登録位置（現在地）</p>
            {fetchedAddress === null ? (
              <p className="text-sm text-primary-300">住所を取得中...</p>
            ) : fetchedAddress !== '' ? (
              <p className="text-sm text-primary-700">{fetchedAddress}</p>
            ) : (
              <p className="text-sm text-primary-300">現在地周辺</p>
            )}
          </div>
        </div>
      )}

      {duplicateWarning && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-yellow-800">⚠️ {duplicateWarning}</p>
          <div className="flex gap-2 mt-2">
            <Button size="sm" onClick={clearWarning}>
              このまま登録
            </Button>
            <Button size="sm" variant="secondary" onClick={() => navigate(-1)}>
              キャンセル
            </Button>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <Input
          label="スポット名"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          maxLength={100}
        />

        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1">カテゴリ *</label>
          <div className="grid grid-cols-3 gap-2">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.value}
                type="button"
                onClick={() => setCategory(cat.value)}
                className={[
                  'flex flex-col items-center gap-1 p-2 rounded-lg border text-xs transition-colors',
                  category === cat.value
                    ? 'border-primary bg-primary-50 text-primary'
                    : 'border-gray-200 hover:border-gray-300',
                ].join(' ')}
              >
                <span className="text-xl">{cat.icon}</span>
                <span>{cat.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1">説明</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={500}
            rows={3}
            placeholder="スポットについての説明を入力"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <Input
          label="住所"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          maxLength={200}
          placeholder="住所（任意）"
        />

        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1">タグ</label>
          <div className="grid grid-cols-2 gap-2">
            {TAGS.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => toggleTag(tag)}
                className={[
                  'flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm transition-colors text-left',
                  selectedTags.includes(tag)
                    ? 'border-primary bg-primary-50 text-primary'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700',
                ].join(' ')}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        {error && <p className="text-sm text-red-500">{error}</p>}

        <Button
          type="submit"
          loading={loading}
          size="lg"
          className="w-full"
          disabled={latitude === null || longitude === null}
        >
          {latitude === null ? '現在地を取得中...' : 'スポットを登録する'}
        </Button>
      </form>
    </div>
  );
}
